/* =============================================================
   Chicago Study Guide — /study/chicago
   Redesigned: 30 schools, advanced filters, clean cost calculator,
   Chicago advantages at top, paginated school list
   ============================================================= */

import { useState, useMemo } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useOrderModal } from "@/contexts/OrderModalContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useConsultation } from "@/contexts/ConsultationModalContext";

// ─── Types ────────────────────────────────────────────────────────────────────

type DegreeType = "language" | "associate" | "bachelor" | "master" | "certificate";
type SchoolType = "university" | "community" | "language" | "vocational";

interface School {
  id: number;
  name: string;
  nameShort?: string;
  type: SchoolType;
  degree: DegreeType[];
  neighborhood: string;
  tuitionMin: number;
  tuitionMax: number;
  duration: number[]; // months: 3, 6, 12, 24, 48
  scholarship: boolean;
  popular?: boolean;
  admissionRate?: string;
  intlStudents?: string;
  majors: string[];
  website: string;
  description: string;
}

// ─── School Data (30 schools) ─────────────────────────────────────────────────

const SCHOOLS: School[] = [
  // === UNIVERSITIES ===
  {
    id: 1, name: "University of Illinois Chicago (UIC)", nameShort: "UIC",
    type: "university", degree: ["bachelor", "master"],
    neighborhood: "Near West Side", tuitionMin: 22000, tuitionMax: 30000,
    duration: [48], scholarship: true, popular: true,
    admissionRate: "79%", intlStudents: "3,500+",
    majors: ["Business", "Engineering", "Health Sciences", "Computer Science", "Liberal Arts"],
    website: "https://www.uic.edu",
    description: "Чикагогийн хамгийн том их сургуулиудын нэг. Хотын төвд байрладаг, олон улсын оюутнуудад тохиромжтой үнэтэй."
  },
  {
    id: 2, name: "DePaul University",
    type: "university", degree: ["bachelor", "master"],
    neighborhood: "Lincoln Park / Loop", tuitionMin: 42000, tuitionMax: 46000,
    duration: [48], scholarship: true, popular: true,
    admissionRate: "74%", intlStudents: "1,800+",
    majors: ["Business", "Computing", "Law", "Liberal Arts", "Education"],
    website: "https://www.depaul.edu",
    description: "АНУ-ын хамгийн том Католик их сургуулиудын нэг. Lincoln Park болон Loop хоёр кампустай."
  },
  {
    id: 3, name: "Loyola University Chicago",
    type: "university", degree: ["bachelor", "master"],
    neighborhood: "Rogers Park", tuitionMin: 48000, tuitionMax: 52000,
    duration: [48], scholarship: true,
    admissionRate: "72%", intlStudents: "1,200+",
    majors: ["Business", "Nursing", "Psychology", "Biology", "Law"],
    website: "https://www.luc.edu",
    description: "Нуурын эрэг дагуу байрлах гоё кампустай. Эрүүл мэнд, бизнесийн чиглэлээр алдартай."
  },
  {
    id: 4, name: "Illinois Institute of Technology (IIT)", nameShort: "IIT",
    type: "university", degree: ["bachelor", "master"],
    neighborhood: "Bronzeville", tuitionMin: 42000, tuitionMax: 50000,
    duration: [48], scholarship: true, popular: true,
    admissionRate: "62%", intlStudents: "2,000+",
    majors: ["Engineering", "Architecture", "Computer Science", "Business", "Design"],
    website: "https://www.iit.edu",
    description: "Инженерчлэл, архитектурын чиглэлээр АНУ-д нэр хүндтэй. Олон улсын оюутнуудын эзлэх хувь өндөр."
  },
  {
    id: 5, name: "Northeastern Illinois University (NEIU)", nameShort: "NEIU",
    type: "university", degree: ["bachelor", "master"],
    neighborhood: "North Side", tuitionMin: 14000, tuitionMax: 18000,
    duration: [48], scholarship: true,
    admissionRate: "55%", intlStudents: "500+",
    majors: ["Education", "Business", "Liberal Arts", "Sciences", "Social Work"],
    website: "https://www.neiu.edu",
    description: "Хамгийн хямд үнэтэй их сургуулиудын нэг. Олон үндэстний оюутнуудтай."
  },
  {
    id: 6, name: "Columbia College Chicago",
    type: "university", degree: ["bachelor", "master"],
    neighborhood: "South Loop", tuitionMin: 28000, tuitionMax: 32000,
    duration: [48], scholarship: true,
    admissionRate: "91%", intlStudents: "800+",
    majors: ["Film", "Music", "Journalism", "Design", "Photography", "Dance"],
    website: "https://www.colum.edu",
    description: "Урлаг, медиа, дизайны чиглэлээр мэргэшсэн. Бүтээлч мэргэжил сонирхогчдод тохиромжтой."
  },
  {
    id: 7, name: "Chicago State University (CSU)", nameShort: "CSU",
    type: "university", degree: ["bachelor", "master"],
    neighborhood: "Roseland", tuitionMin: 12000, tuitionMax: 16000,
    duration: [48], scholarship: true,
    admissionRate: "68%", intlStudents: "300+",
    majors: ["Education", "Business", "Health Sciences", "Liberal Arts"],
    website: "https://www.csu.edu",
    description: "Хамгийн хямд их сургуулиудын нэг. Олон улсын оюутнуудад тусгай дэмжлэг үзүүлдэг."
  },
  {
    id: 8, name: "Concordia University Chicago",
    type: "university", degree: ["bachelor", "master"],
    neighborhood: "River Forest", tuitionMin: 30000, tuitionMax: 36000,
    duration: [48], scholarship: true,
    admissionRate: "70%", intlStudents: "400+",
    majors: ["Education", "Business", "Psychology", "Theology", "Sports Management"],
    website: "https://www.cuchicago.edu",
    description: "Чикагогийн баруун захад байрлах тайван кампустай. Боловсрол, сэтгэл судлалын чиглэлээр алдартай."
  },
  {
    id: 9, name: "Roosevelt University",
    type: "university", degree: ["bachelor", "master"],
    neighborhood: "Loop", tuitionMin: 28000, tuitionMax: 34000,
    duration: [48], scholarship: true,
    admissionRate: "67%", intlStudents: "600+",
    majors: ["Business", "Music", "Psychology", "Law", "Education"],
    website: "https://www.roosevelt.edu",
    description: "Хотын төвд байрладаг. Хөгжим, бизнесийн чиглэлээр алдартай. Олон улсын оюутнуудад тохиромжтой."
  },
  {
    id: 10, name: "North Park University",
    type: "university", degree: ["bachelor", "master"],
    neighborhood: "North Park", tuitionMin: 28000, tuitionMax: 32000,
    duration: [48], scholarship: true,
    admissionRate: "65%", intlStudents: "350+",
    majors: ["Business", "Nursing", "Education", "Biology", "Music"],
    website: "https://www.northpark.edu",
    description: "Аюулгүй, тохилог хороолол дахь жижиг их сургууль. Олон улсын оюутнуудад дотно орчин."
  },
  // === COMMUNITY COLLEGES ===
  {
    id: 11, name: "City Colleges of Chicago (CCC)", nameShort: "CCC",
    type: "community", degree: ["associate", "language"],
    neighborhood: "Бүх дүүрэгт", tuitionMin: 8000, tuitionMax: 12000,
    duration: [12, 24], scholarship: true, popular: true,
    admissionRate: "Open", intlStudents: "2,000+",
    majors: ["ESL", "Business", "Healthcare", "Technology", "Transfer Programs"],
    website: "https://www.ccc.edu",
    description: "7 кампустай, хамгийн хямд сонголт. 2 жил суралцаад их сургуульд шилжих боломжтой."
  },
  {
    id: 12, name: "College of DuPage",
    type: "community", degree: ["associate", "certificate"],
    neighborhood: "Glen Ellyn (suburb)", tuitionMin: 9000, tuitionMax: 13000,
    duration: [12, 24], scholarship: true,
    admissionRate: "Open", intlStudents: "800+",
    majors: ["Business", "Healthcare", "Technology", "Arts", "Sciences"],
    website: "https://www.cod.edu",
    description: "Чикагогийн хойд захын том community college. Их сургуульд шилжих хөтөлбөртэй."
  },
  {
    id: 13, name: "Oakton Community College",
    type: "community", degree: ["associate", "certificate"],
    neighborhood: "Des Plaines (suburb)", tuitionMin: 8500, tuitionMax: 11000,
    duration: [12, 24], scholarship: false,
    admissionRate: "Open", intlStudents: "500+",
    majors: ["Business", "Computer Science", "Healthcare", "Engineering Transfer"],
    website: "https://www.oakton.edu",
    description: "Хойд Чикагогийн хямд community college. Инженерчлэл, эрүүл мэндийн чиглэлтэй."
  },
  {
    id: 14, name: "Moraine Valley Community College",
    type: "community", degree: ["associate", "certificate"],
    neighborhood: "Palos Hills (suburb)", tuitionMin: 7500, tuitionMax: 10000,
    duration: [12, 24], scholarship: false,
    admissionRate: "Open", intlStudents: "400+",
    majors: ["Business", "Healthcare", "Technology", "Liberal Arts"],
    website: "https://www.morainevalley.edu",
    description: "Өмнөд Чикагогийн хямд community college. Эрүүл мэндийн чиглэлтэй."
  },
  {
    id: 15, name: "Harper College",
    type: "community", degree: ["associate", "certificate"],
    neighborhood: "Palatine (suburb)", tuitionMin: 8000, tuitionMax: 11500,
    duration: [12, 24], scholarship: true,
    admissionRate: "Open", intlStudents: "600+",
    majors: ["Business", "Computer Science", "Engineering", "Healthcare", "Arts"],
    website: "https://www.harpercollege.edu",
    description: "Хойд Чикагогийн нэр хүндтэй community college. Технологийн чиглэлтэй."
  },
  // === VOCATIONAL / CAREER COLLEGES ===
  {
    id: 16, name: "Midwestern Career College",
    type: "vocational", degree: ["associate", "certificate"],
    neighborhood: "Loop (Downtown)", tuitionMin: 10000, tuitionMax: 18000,
    duration: [12, 24], scholarship: false, popular: true,
    admissionRate: "Open", intlStudents: "400+",
    majors: ["Medical Assisting", "Business Administration", "Accounting", "Marketing", "Healthcare"],
    website: "https://mccollege.edu",
    description: "Хотын төвд байрладаг мэргэжлийн сургууль. 400+ F-1 оюутан, 45+ улсаас ирсэн оюутнуудтай. Эмнэлгийн туслах, бизнесийн чиглэлтэй."
  },
  {
    id: 17, name: "Computer Systems Institute (CSI)", nameShort: "CSI",
    type: "vocational", degree: ["certificate", "associate"],
    neighborhood: "Skokie / Lombard", tuitionMin: 6000, tuitionMax: 14000,
    duration: [6, 12, 24], scholarship: true, popular: true,
    admissionRate: "Open", intlStudents: "1,000+",
    majors: ["ESL", "Computer Networking", "IT Support", "Business", "Customer Service"],
    website: "https://www.csinow.edu",
    description: "100+ улсаас ирсэн оюутнуудтай. ESL-ээс компьютерийн мэргэжил хүртэл. Skokie болон Lombard кампустай."
  },
  {
    id: 18, name: "Coyne College",
    type: "vocational", degree: ["associate", "certificate"],
    neighborhood: "Near North Side", tuitionMin: 12000, tuitionMax: 20000,
    duration: [12, 24], scholarship: false,
    admissionRate: "Open", intlStudents: "200+",
    majors: ["HVAC", "Electrical Technology", "Medical Assisting", "Dental Assisting"],
    website: "https://www.coynecollege.edu",
    description: "Техникийн мэргэжлийн сургууль. HVAC, цахилгааны инженерчлэлийн чиглэлтэй."
  },
  {
    id: 19, name: "Generations College",
    type: "vocational", degree: ["certificate", "language"],
    neighborhood: "Skokie", tuitionMin: 5000, tuitionMax: 10000,
    duration: [3, 6, 12], scholarship: true,
    admissionRate: "Open", intlStudents: "300+",
    majors: ["ESL", "Business English", "Computer Skills", "Hospitality"],
    website: "https://www.generations.edu",
    description: "Олон улсын оюутнуудад зориулсан хямд сургууль. ESL болон мэргэжлийн сертификатын хөтөлбөртэй."
  },
  {
    id: 20, name: "Westwood College Chicago",
    type: "vocational", degree: ["associate", "bachelor"],
    neighborhood: "O'Hare / South Loop", tuitionMin: 18000, tuitionMax: 28000,
    duration: [24, 48], scholarship: true,
    admissionRate: "Open", intlStudents: "400+",
    majors: ["Business", "Criminal Justice", "Design", "Technology"],
    website: "https://www.westwood.edu",
    description: "Мэргэжлийн чиглэлтэй 4 жилийн сургууль. Хоёр кампустай."
  },
  // === LANGUAGE SCHOOLS ===
  {
    id: 21, name: "ELS Language Centers Chicago",
    type: "language", degree: ["language"],
    neighborhood: "Loop", tuitionMin: 6000, tuitionMax: 10000,
    duration: [3, 6, 12], scholarship: false, popular: true,
    admissionRate: "Open", intlStudents: "300+",
    majors: ["Intensive English", "TOEFL Prep", "University Pathway", "Business English"],
    website: "https://www.els.edu",
    description: "Англи хэлний эрчимт сургалт. Их сургуульд элсэхийн өмнө хэлний түвшинг дээшлүүлэх хамгийн сайн сонголт."
  },
  {
    id: 22, name: "Kaplan International Chicago",
    type: "language", degree: ["language"],
    neighborhood: "Loop", tuitionMin: 5000, tuitionMax: 9000,
    duration: [3, 6, 12], scholarship: false,
    admissionRate: "Open", intlStudents: "400+",
    majors: ["General English", "IELTS/TOEFL", "Academic English", "Conversation"],
    website: "https://www.kaplaninternational.com",
    description: "Дэлхийд алдартай Kaplan брэндийн Чикаго дахь төв. Уян хатан хуваарьтай."
  },
  {
    id: 23, name: "EC English Chicago",
    type: "language", degree: ["language"],
    neighborhood: "River North", tuitionMin: 5500, tuitionMax: 9500,
    duration: [3, 6, 12], scholarship: false,
    admissionRate: "Open", intlStudents: "350+",
    majors: ["General English", "Business English", "IELTS Prep", "Academic English"],
    website: "https://www.ecenglish.com",
    description: "Хотын төвд байрлах орчин үеийн хэлний сургууль. Олон улсын оюутнуудад тохиромжтой."
  },
  {
    id: 24, name: "Language Systems International Chicago",
    type: "language", degree: ["language"],
    neighborhood: "Loop", tuitionMin: 4500, tuitionMax: 8000,
    duration: [3, 6, 12], scholarship: false,
    admissionRate: "Open", intlStudents: "250+",
    majors: ["Intensive English", "TOEFL Prep", "University Transfer"],
    website: "https://www.lsi.edu",
    description: "Хямд үнэтэй хэлний сургууль. Их сургуульд шилжих хөтөлбөртэй."
  },
  {
    id: 25, name: "Intrax English Institute Chicago",
    type: "language", degree: ["language"],
    neighborhood: "Streeterville", tuitionMin: 5000, tuitionMax: 8500,
    duration: [3, 6, 12], scholarship: false,
    admissionRate: "Open", intlStudents: "300+",
    majors: ["General English", "Business English", "TOEFL/IELTS", "Academic Prep"],
    website: "https://www.intraxenglish.com",
    description: "Мичиган нуурын ойролцоо байрлах хэлний сургууль. Бизнесийн англи хэлний чиглэлтэй."
  },
  {
    id: 26, name: "Chicago English Academy",
    type: "language", degree: ["language"],
    neighborhood: "Downtown", tuitionMin: 4000, tuitionMax: 7500,
    duration: [3, 6, 12], scholarship: false,
    admissionRate: "Open", intlStudents: "200+",
    majors: ["Intensive English", "Conversation", "TOEFL Prep"],
    website: "https://www.chicagoenglishacademy.com",
    description: "Жижиг ангитай, хувийн хандлагатай хэлний сургууль."
  },
  // === GRADUATE / SPECIALIZED ===
  {
    id: 27, name: "The Chicago School of Professional Psychology",
    type: "university", degree: ["master"],
    neighborhood: "Loop", tuitionMin: 22000, tuitionMax: 35000,
    duration: [24, 48], scholarship: true,
    admissionRate: "65%", intlStudents: "400+",
    majors: ["Clinical Psychology", "Counseling", "Forensic Psychology", "Applied Behavior Analysis"],
    website: "https://www.thechicagoschool.edu",
    description: "Сэтгэл судлалын чиглэлд мэргэшсэн. Клиник, зөвлөх сэтгэл судлалын магистрын хөтөлбөртэй."
  },
  {
    id: 28, name: "National Louis University (NLU)", nameShort: "NLU",
    type: "university", degree: ["bachelor", "master"],
    neighborhood: "Chicago / Wheeling", tuitionMin: 16000, tuitionMax: 24000,
    duration: [48], scholarship: true,
    admissionRate: "72%", intlStudents: "500+",
    majors: ["Education", "Business", "Psychology", "Human Services"],
    website: "https://www.nl.edu",
    description: "Боловсролын чиглэлд мэргэшсэн. Хямд үнэтэй, олон улсын оюутнуудад тохиромжтой."
  },
  {
    id: 29, name: "Kendall College (Laureate)",
    type: "university", degree: ["associate", "bachelor"],
    neighborhood: "Near North Side", tuitionMin: 24000, tuitionMax: 32000,
    duration: [24, 48], scholarship: true,
    admissionRate: "78%", intlStudents: "600+",
    majors: ["Culinary Arts", "Hospitality Management", "Business", "Early Childhood Education"],
    website: "https://www.kendall.edu",
    description: "Хоол хүнс, зочлон байрлуулах чиглэлд мэргэшсэн. Дэлхийн хэмжээний Laureate сүлжээний нэг."
  },
  {
    id: 30, name: "Tribeca Flashpoint College",
    type: "vocational", degree: ["associate", "bachelor"],
    neighborhood: "Loop", tuitionMin: 20000, tuitionMax: 30000,
    duration: [24, 48], scholarship: true,
    admissionRate: "Open", intlStudents: "300+",
    majors: ["Film Production", "Game Design", "Audio Production", "Animation"],
    website: "https://www.tribecaflashpoint.edu",
    description: "Медиа, дизайн, тоглоом хөгжүүлэлтийн чиглэлд мэргэшсэн. Бүтээлч мэргэжил сонирхогчдод тохиромжтой."
  },
];

// ─── Cost Calculator ───────────────────────────────────────────────────────────

// tuitionMonthly = жилийн төлбөр / 12
const TUITION_OPTIONS = [
  { label: "Хэлний курс — $500–$700/сар", annualValue: 7200, monthlyValue: 600 },
  { label: "Community College — $700–$900/сар", annualValue: 9600, monthlyValue: 800 },
  { label: "Мэргэжлийн сургууль — $900–$1,100/сар", annualValue: 12000, monthlyValue: 1000 },
  { label: "Их сургууль хямд (NEIU, CSU) — $1,200–$1,500/сар", annualValue: 16800, monthlyValue: 1400 },
  { label: "Их сургууль дунд (UIC, DePaul) — $2,000–$2,500/сар", annualValue: 27600, monthlyValue: 2300 },
  { label: "Их сургууль өндөр (Loyola, IIT) — $3,500–$4,300/сар", annualValue: 46800, monthlyValue: 3900 },
];

const DURATION_OPTIONS = [
  { label: "3 сар", months: 3 },
  { label: "6 сар", months: 6 },
  { label: "12 сар (1 жил)", months: 12 },
  { label: "24 сар (2 жил)", months: 24 },
  { label: "48 сар (4 жил)", months: 48 },
];

const FAMILY_OPTIONS = [
  { label: "Ганцаараа", adults: 1, children: 0, rentBase: 1100, foodBase: 350 },
  { label: "Хосоороо (2 насанд хүрэгч)", adults: 2, children: 0, rentBase: 1600, foodBase: 600 },
  { label: "Гэр бүл (2 насанд хүрэгч + 1 хүүхэд)", adults: 2, children: 1, rentBase: 2000, foodBase: 800 },
  { label: "Гэр бүл (2 насанд хүрэгч + 2 хүүхэд)", adults: 2, children: 2, rentBase: 2400, foodBase: 1000 },
];

// ─── Chicago Advantages ───────────────────────────────────────────────────────

const ADVANTAGES = [
  {
    icon: "🇲🇳",
    title: "Хамгийн олон Монголчууд",
    desc: "АНУ-д суралцаж буй Монгол оюутнуудын хамгийн их тоо Чикагод байдаг. Glenview, Northbrook, Skokie дүүрэгт Монгол айлуудын нийгэмлэг, Монгол хоолны газар, дэлгүүр байдаг. Шинэ оюутнуудад нийгэмлэг маш их туслалцаа үзүүлдэг."
  },
  {
    icon: "💰",
    title: "Хамгийн боломжийн зардал",
    desc: "Нью-Йоркоос 40%, Сан-Францискоос 50% хямд амьдрах зардалтай. NEIU, City Colleges зэрэг хямд сургуулиуд байдаг. Оюутны орон сууц $900–$1,400/сар олдоно."
  },
  {
    icon: "🏫",
    title: "Хүүхдийн сургууль үнэгүй",
    desc: "Гэр бүлээрээ ирвэл K-12 (1–12-р анги) нийтийн сургуульд хүүхэд үнэгүй сурна. Chicago Public Schools нь АНУ-ын 3-р том сургуулийн систем. Олон хэлний дэмжлэгтэй, олон улсын оюутнуудад ээлтэй."
  },
  {
    icon: "🏙️",
    title: "АНУ-ын 3-р том хот",
    desc: "Нью-Йорк, Лос-Анжелесийн дараа АНУ-ын 3-р том хот. Finance, Tech, Healthcare, Logistics салбарт ажлын байр элбэг. CPT/OPT-ээр ажиллах боломж өндөр."
  },
  {
    icon: "🎓",
    title: "30+ нэр хүндтэй сургууль",
    desc: "UIC, IIT, DePaul, Loyola, Northwestern зэрэг дэлхийд танигдсан их сургуулиуд нэг хотод. Хэлний курсээс магистр хүртэл бүх шатны боловсрол авах боломжтой."
  },
  {
    icon: "🚇",
    title: "Машингүй амьдрах боломж",
    desc: "CTA метро, автобусны сүлжээ хотын бүх хэсгийг холбодог. Ventra Card-аар сарын $105-д хязгааргүй тээвэр. Машин худалдаж авах, даатгал, паркинг зардал байхгүй."
  },
];

// ─── FAQ Data ─────────────────────────────────────────────────────────────────

const FAQS = [
  {
    q: "F-1 визтэй Чикагод ажиллаж болох уу?",
    a: "Тийм. On-campus ажил (кампус дотор) хязгааргүй зөвшөөрөгдөнө. Off-campus ажлын хувьд: CPT (Curricular Practical Training) — сургалтын хэсэг болгон ажиллах, OPT (Optional Practical Training) — төгсөлтийн дараа 12 сар (STEM мэргэжлийн хувьд 36 сар) ажиллах боломжтой."
  },
  {
    q: "Чикагод хэдэн доллар авчрах хэрэгтэй вэ?",
    a: "Консулын шаардлагаар нэг жилийн сургалтын төлбөр + амьдрах зардлыг нотлох ёстой. Ойролцоогоор $25,000–$55,000 (сургуулиас хамаарч). Банкны дансны хуулга эсвэл тэтгэлгийн захидал шаардагдана."
  },
  {
    q: "Чикагогийн өвөл хэр хүйтэн байдаг вэ?",
    a: "Чикаго нь 'Windy City' хочтой. Өвлийн дундаж температур -10°C хүртэл буурдаг. Гэхдээ дотоод тээвэр (метро, подвал холбоос) сайн тул хүйтэнд тийм ч их өртдөггүй. Монголчуудад хүйтэн тийм ч асуудал биш!"
  },
  {
    q: "Чикагод Монгол хүн байдаг уу?",
    a: "Тийм. Glenview, Northbrook, Skokie дүүрэгт Монгол айлуудын нийгэмлэг байдаг. Монгол хоолны газар, дэлгүүр, Facebook бүлэг байдаг. Шинэ оюутнуудад нийгэмлэг маш их туслалцаа үзүүлдэг."
  },
  {
    q: "Хэлний сургуулиас их сургуульд шилжих боломжтой юу?",
    a: "Тийм, энэ бол маш түгээмэл зам. ELS, Kaplan, CSI зэрэг хэлний сургуулиуд их сургуультай pathway гэрээтэй. TOEFL/IELTS шаардлага хангасны дараа шилжин суралцах боломжтой."
  },
  {
    q: "Чикагод гэр бүлийнхэнтэйгээ хамт очиж болох уу?",
    a: "Тийм. F-2 виз авбал эхнэр/нөхөр, 21 хүртэлх насны хүүхдүүд дагалдан очиж болно. F-2 эзэмшигч ажиллах эрхгүй боловч сургуульд суралцах боломжтой. Гэр бүлийн зардал мэдэгдэхүйц нэмэгддэг тул тооцоолуурыг ашиглана уу."
  },
  {
    q: "Чикагод эрүүл мэндийн даатгал хэрэгтэй юу?",
    a: "Тийм, заавал шаардагдана. Ихэнх их сургуулиуд өөрийн даатгалын хөтөлбөртэй ($1,500–$3,000/жил). Зарим оюутнууд Marketplace даатгал авдаг. Даатгалгүй байх нь маш эрсдэлтэй — эмнэлгийн зардал АНУ-д маш өндөр."
  },
  {
    q: "United Dream Mongolia Чикагод суралцахад яаж туслах вэ?",
    a: "Бид F-1 оюутны визийн бүрэн үйлчилгээ үзүүлнэ: сургуулийн сонголт, элсэлтийн баримт бичиг бэлдэх, I-20 авах, визийн ярилцлагад бэлтгэх, зочид буудал, нисэх тийз зохицуулах. Нэг дор бүх зүйлийг шийдэж өгнө."
  },
];

// ─── Main Component ───────────────────────────────────────────────────────────

const SCHOOLS_PER_PAGE = 9;

export default function ChicagoStudyGuide() {
  const { openConsultation } = useConsultation();
  const openModal = useOrderModal();

  // School filters
  const [filterDegree, setFilterDegree] = useState<DegreeType | "all">("all");
  const [filterBudget, setFilterBudget] = useState<"all" | "low" | "mid" | "high">("all");
  const [filterScholarship, setFilterScholarship] = useState(false);
  const [filterDuration, setFilterDuration] = useState<number | "all">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Cost calculator
  const [calcTuitionIdx, setCalcTuitionIdx] = useState(3); // default: хямд их сургууль
  const [calcDuration, setCalcDuration] = useState(12);
  const [calcFamily, setCalcFamily] = useState(0);

  const familyOption = FAMILY_OPTIONS[calcFamily];
  const selectedTuition = TUITION_OPTIONS[calcTuitionIdx];

  const filteredSchools = useMemo(() => {
    return SCHOOLS.filter(s => {
      if (filterDegree !== "all" && !s.degree.includes(filterDegree)) return false;
      if (filterBudget === "low" && s.tuitionMin > 15000) return false;
      if (filterBudget === "mid" && (s.tuitionMin < 15000 || s.tuitionMax > 35000)) return false;
      if (filterBudget === "high" && s.tuitionMax < 35000) return false;
      if (filterScholarship && !s.scholarship) return false;
      if (filterDuration !== "all" && !s.duration.includes(filterDuration as number)) return false;
      if (searchQuery && !s.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !s.majors.some(m => m.toLowerCase().includes(searchQuery.toLowerCase()))) return false;
      return true;
    });
  }, [filterDegree, filterBudget, filterScholarship, filterDuration, searchQuery]);

  const totalPages = Math.ceil(filteredSchools.length / SCHOOLS_PER_PAGE);
  const pagedSchools = filteredSchools.slice(
    (currentPage - 1) * SCHOOLS_PER_PAGE,
    currentPage * SCHOOLS_PER_PAGE
  );

  // Reset to page 1 when filters change
  const handleFilterChange = (fn: () => void) => {
    fn();
    setCurrentPage(1);
  };

  // Cost calculation
  const monthlyLiving = familyOption.rentBase + familyOption.foodBase + 120 + 200;
  const totalLiving = monthlyLiving * calcDuration;
  const tuitionForDuration = selectedTuition.monthlyValue * calcDuration;
  const grandTotal = tuitionForDuration + totalLiving;
  const grandTotalMNT = grandTotal * 3500;
  // Visa fees (separate from tuition)
  const sevisFee = 350;
  const visaFee = 185;
  const appFee = 250;
  const totalVisaFees = sevisFee + visaFee + appFee;

  const degreeLabels: Record<DegreeType | "all", string> = {
    all: "Бүгд",
    language: "Хэлний курс",
    associate: "Associate (2 жил)",
    bachelor: "Бакалавр",
    master: "Магистр",
    certificate: "Сертификат",
  };

  const durationLabels: Record<string, string> = {
    all: "Бүх хугацаа",
    "3": "3 сар",
    "6": "6 сар",
    "12": "12 сар",
    "24": "24 сар",
    "48": "48 сар",
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* ── Hero ── */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white pt-24 pb-12 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap gap-2 mb-4">
            <Link href="/">
              <span className="text-blue-300 text-sm hover:underline cursor-pointer">← Нүүр хуудас</span>
            </Link>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            <Badge className="bg-blue-600 text-white">🇺🇸 АНУ — Чикаго хот</Badge>
            <Badge className="bg-slate-700 text-white">F-1 Оюутны виз</Badge>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-3">
            Чикагод <span className="text-blue-400">суралцах</span> бүрэн гарын авлага
          </h1>
          <p className="text-slate-300 text-lg mb-6 max-w-2xl">
            30+ сургуулийн жагсаалт, зардлын тооцоолол, хороолол сонголт — Монгол оюутнуудад зориулсан.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => openModal("us-study")} className="bg-blue-600 hover:bg-blue-700 text-white">
              Виз мэдүүлэх
            </Button>
            <Button variant="outline" className="border-white text-white hover:bg-white/10"
              onClick={() => document.getElementById("schools")?.scrollIntoView({ behavior: "smooth" })}>
              Сургуулиуд харах ↓
            </Button>
          </div>
        </div>
      </section>

      {/* ── Chicago Advantages ── */}
      <section className="bg-blue-50 py-10 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl font-bold text-slate-800 mb-6 text-center">✨ Яагаад Чикаго вэ?</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {ADVANTAGES.map((adv, i) => (
              <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-blue-100">
                <div className="text-2xl mb-2">{adv.icon}</div>
                <div className="font-semibold text-slate-800 text-sm mb-1">{adv.title}</div>
                <div className="text-slate-500 text-xs leading-relaxed">{adv.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Schools Section ── */}
      <section id="schools" className="py-12 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">🎓 Чикагогийн сургуулиуд</h2>
          <p className="text-slate-500 mb-6 text-sm">30 сургуулийн мэдээлэл — шүүлтүүр ашиглан хайна уу</p>

          {/* Filters */}
          <div className="bg-slate-50 rounded-xl p-4 mb-6 space-y-3">
            {/* Search */}
            <input
              type="text"
              placeholder="🔍 Сургуулийн нэр эсвэл мэргэжлээр хайх..."
              value={searchQuery}
              onChange={e => handleFilterChange(() => setSearchQuery(e.target.value))}
              className="w-full border border-slate-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />

            {/* Degree Type */}
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Боловсролын зэрэг</div>
              <div className="flex flex-wrap gap-2">
                {(["all", "language", "associate", "bachelor", "master", "certificate"] as const).map(d => (
                  <button
                    key={d}
                    onClick={() => handleFilterChange(() => setFilterDegree(d))}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      filterDegree === d
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-slate-600 border-slate-200 hover:border-blue-400"
                    }`}
                  >
                    {degreeLabels[d]}
                  </button>
                ))}
              </div>
            </div>

            {/* Duration */}
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Хугацаа</div>
              <div className="flex flex-wrap gap-2">
                {(["all", 3, 6, 12, 24, 48] as const).map(d => (
                  <button
                    key={d}
                    onClick={() => handleFilterChange(() => setFilterDuration(d))}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      filterDuration === d
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-white text-slate-600 border-slate-200 hover:border-indigo-400"
                    }`}
                  >
                    {durationLabels[String(d)]}
                  </button>
                ))}
              </div>
            </div>

            {/* Budget + Scholarship */}
            <div className="flex flex-wrap gap-4 items-center">
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Үнэ</div>
                <div className="flex gap-2">
                  {(["all", "low", "mid", "high"] as const).map(b => (
                    <button
                      key={b}
                      onClick={() => handleFilterChange(() => setFilterBudget(b))}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        filterBudget === b
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400"
                      }`}
                    >
                      {b === "all" ? "Бүх үнэ" : b === "low" ? "Хямд ($15K↓)" : b === "mid" ? "Дунд ($15K–$35K)" : "Өндөр ($35K+)"}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <input
                  type="checkbox"
                  id="scholarship"
                  checked={filterScholarship}
                  onChange={e => handleFilterChange(() => setFilterScholarship(e.target.checked))}
                  className="w-4 h-4 accent-blue-600"
                />
                <label htmlFor="scholarship" className="text-sm text-slate-700 cursor-pointer">
                  🏆 Тэтгэлэг авах боломжтой
                </label>
              </div>
            </div>
          </div>

          {/* Results count */}
          <div className="text-sm text-slate-500 mb-4">
            {filteredSchools.length} сургууль олдлоо
            {filteredSchools.length > SCHOOLS_PER_PAGE && (
              <span> · Хуудас {currentPage}/{totalPages}</span>
            )}
          </div>

          {/* School Cards */}
          {pagedSchools.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <div className="text-4xl mb-3">🔍</div>
              <div>Шүүлтүүрт тохирох сургууль олдсонгүй</div>
              <button onClick={() => {
                setFilterDegree("all"); setFilterBudget("all");
                setFilterScholarship(false); setFilterDuration("all");
                setSearchQuery(""); setCurrentPage(1);
              }} className="mt-3 text-blue-600 hover:underline text-sm">
                Шүүлтүүр цэвэрлэх
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pagedSchools.map(school => (
                <div key={school.id} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow flex flex-col">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex flex-wrap gap-1 mb-1">
                        {school.popular && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs px-2 py-0">⭐ Алдартай</Badge>
                        )}
                        {school.scholarship && (
                          <Badge className="bg-green-100 text-green-700 text-xs px-2 py-0">🏆 Тэтгэлэг</Badge>
                        )}
                      </div>
                      <h3 className="font-bold text-slate-800 text-sm leading-tight">{school.name}</h3>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                    <span>📍 {school.neighborhood}</span>
                    <span>·</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      school.type === "university" ? "bg-blue-100 text-blue-700" :
                      school.type === "community" ? "bg-purple-100 text-purple-700" :
                      school.type === "language" ? "bg-green-100 text-green-700" :
                      "bg-orange-100 text-orange-700"
                    }`}>
                      {school.type === "university" ? "Их сургууль" :
                       school.type === "community" ? "Community College" :
                       school.type === "language" ? "Хэлний сургууль" : "Мэргэжлийн"}
                    </span>
                  </div>

                  <p className="text-xs text-slate-600 mb-3 leading-relaxed flex-1">{school.description}</p>

                  <div className="bg-slate-50 rounded-lg p-2 mb-3 text-xs">
                    <div className="flex justify-between mb-1">
                      <span className="text-slate-500">Сургалтын төлбөр</span>
                      <span className="font-semibold text-slate-800">
                        ${school.tuitionMin.toLocaleString()}–${school.tuitionMax.toLocaleString()}/жил
                      </span>
                    </div>
                    {school.admissionRate && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Элсэлт</span>
                        <span className="font-medium text-slate-700">{school.admissionRate}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-1 mb-3">
                    {school.majors.slice(0, 3).map(m => (
                      <span key={m} className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full">{m}</span>
                    ))}
                    {school.majors.length > 3 && (
                      <span className="text-slate-400 text-xs px-1">+{school.majors.length - 3}</span>
                    )}
                  </div>

                  <div className="flex gap-2 mt-auto">
                    <a
                      href={school.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-center text-xs py-2 px-3 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
                    >
                      Дэлгэрэнгүй →
                    </a>
                    <a
                      href="#" onClick={(e) => { e.preventDefault(); openConsultation(); }}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-xs py-2 px-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center"
                    >
                      Зөвлөгөө авах
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 rounded-lg border text-sm disabled:opacity-40 hover:bg-slate-50"
              >
                ← Өмнөх
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  className={`w-9 h-9 rounded-lg text-sm font-medium ${
                    p === currentPage ? "bg-blue-600 text-white" : "border hover:bg-slate-50 text-slate-700"
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 rounded-lg border text-sm disabled:opacity-40 hover:bg-slate-50"
              >
                Дараах →
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── Cost Calculator ── */}
      <section className="py-12 px-4 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">💰 Зардлын тооцоолол</h2>
          <p className="text-slate-500 mb-8 text-sm">Сургуулийн төрөл, хугацаа, гэр бүлийн байдлаа сонгоод нийт зардлаа тооцоол</p>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {/* Tuition */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">📚 Сургуулийн төрөл</label>
                <select
                  value={calcTuitionIdx}
                  onChange={e => setCalcTuitionIdx(Number(e.target.value))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                >
                  {TUITION_OPTIONS.map((opt, i) => (
                    <option key={i} value={i}>{opt.label}</option>
                  ))}
                </select>
                <div className="text-xs text-slate-400 mt-1">Сарын сургалтын төлбөр: ${selectedTuition.monthlyValue.toLocaleString()}</div>
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">📅 Суралцах хугацаа</label>
                <select
                  value={calcDuration}
                  onChange={e => setCalcDuration(Number(e.target.value))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                >
                  {DURATION_OPTIONS.map(opt => (
                    <option key={opt.months} value={opt.months}>{opt.label}</option>
                  ))}
                </select>
                <div className="text-xs text-slate-400 mt-1">{calcDuration} сарын нийт зардал</div>
              </div>

              {/* Family */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">👨‍👩‍👧 Гэр бүлийн байдал</label>
                <select
                  value={calcFamily}
                  onChange={e => setCalcFamily(Number(e.target.value))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                >
                  {FAMILY_OPTIONS.map((opt, i) => (
                    <option key={i} value={i}>{opt.label}</option>
                  ))}
                </select>
                <div className="text-xs text-slate-400 mt-1">Сарын амьдрах зардал: ${monthlyLiving.toLocaleString()}</div>
              </div>
            </div>

            {/* Results */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100">
              <h3 className="font-bold text-slate-800 mb-4 text-base">📊 Нийт зардлын тооцоолол</h3>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Сургалтын төлбөр ({calcDuration} сар × ${selectedTuition.monthlyValue.toLocaleString()})</span>
                  <span className="font-semibold text-slate-800">${tuitionForDuration.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Орон сууц ({calcDuration} сар × ${familyOption.rentBase.toLocaleString()})</span>
                  <span className="font-semibold text-slate-800">${(familyOption.rentBase * calcDuration).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Хоол хүнс ({calcDuration} сар × ${familyOption.foodBase.toLocaleString()})</span>
                  <span className="font-semibold text-slate-800">${(familyOption.foodBase * calcDuration).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Тээвэр + бусад ({calcDuration} сар × $320)</span>
                  <span className="font-semibold text-slate-800">${(320 * calcDuration).toLocaleString()}</span>
                </div>
                <div className="border-t border-blue-200 pt-2 flex justify-between">
                  <span className="font-bold text-slate-800">Нийт дүн</span>
                  <span className="font-bold text-blue-700 text-lg">${grandTotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm text-slate-500">
                  <span>Монгол төгрөгөөр (~₮3,500/$)</span>
                  <span className="font-medium">₮{grandTotalMNT.toLocaleString()}</span>
                </div>
              </div>

              {familyOption.children > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 mb-3">
                  ⚠️ Хүүхдэтэй гэр бүлийн хувьд: хүүхдийн эрүүл мэндийн даатгал (+$200/сар), хүүхдийн нийтийн сургууль үнэгүй тул хүүхдийн зардал багасана.
                </div>
              )}

              <div className="text-xs text-slate-400">* Тооцоолол ойролцоо дүн. Сургуулийн байршил, амьдрах хэв маягаас хамаарч өөрчлөгдөж болно.</div>
            </div>

            {/* Visa Fees Section */}
            <div className="mt-6 bg-indigo-50 border border-indigo-200 rounded-xl p-5">
              <h3 className="font-bold text-indigo-900 mb-1 text-base">💳 Виз мэдүүлэхийн зардал <span className="text-xs font-normal text-indigo-600 ml-1">(сургалтын төлбөрт хамаарахгүй)</span></h3>
              <p className="text-xs text-indigo-700 mb-4">Виз авахаас урьдчилан төлбөх зардал — сургалтын төлбөр виз гарсаны дараа төлдөг болно — чухал давуу тал!</p>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-indigo-700">SEVIS хурдасын хүлсэн гэрчилгээн төлбөр</span>
                  <span className="font-semibold text-indigo-900">${sevisFee}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-indigo-700">F-1 визний хүсэлтийн хурдас (MRV Fee)</span>
                  <span className="font-semibold text-indigo-900">${visaFee}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-indigo-700">Сургуулийн бүртгүүлэлтийн хүсэлт (Application Fee)</span>
                  <span className="font-semibold text-indigo-900">Хүртэл $250</span>
                </div>
                <div className="border-t border-indigo-200 pt-2 flex justify-between">
                  <span className="font-bold text-indigo-900">Визний зардалын нийт дүн</span>
                  <span className="font-bold text-indigo-700 text-lg">~${totalVisaFees}</span>
                </div>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-800">
                ✅ <strong>Давуу тал:</strong> АНУ-д суралцахаас сургуулын сургалтын төлбөрийг виз авахаасаа урьдчилан төлх шаардлагагүй! Зөвхөн зүгээр SEVIS + Visa fee төлбөрийг төлсөн виз авна. Виз гарсаны дараа сургууль төлбөрийг төлдөг болно — ирлэнд виз авахгүй бол бол санаа зардал алдагдахгүй.
              </div>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <a
                href="#" onClick={(e) => { e.preventDefault(); openConsultation(); }}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-sm transition-colors"
              >
                Зөвлөгөө авах — Чикагод суралцах
              </a>
            </div>
          </div>

          {/* Tips */}
          <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-4">
            <h4 className="font-semibold text-green-800 mb-3 text-sm">💡 Зардал хэмнэх зөвлөмжүүд</h4>
            <div className="grid sm:grid-cols-2 gap-2 text-xs text-green-700">
              <div>✓ City Colleges-д 2 жил суралцаад их сургуульд шилжих — $20,000+ хэмнэнэ</div>
              <div>✓ Rogers Park, Albany Park — хамгийн хямд түрээс ($900–$1,200)</div>
              <div>✓ CTA Ventra Card — сарын $105-д хязгааргүй тээвэр</div>
              <div>✓ Кампус дотор ажиллах — сард $500–$800 нэмэлт орлого</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-800 mb-6">❓ Түгээмэл асуулт & хариулт</h2>
          <Accordion type="single" collapsible className="space-y-2">
            {FAQS.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-slate-200 rounded-xl px-4">
                <AccordionTrigger className="text-sm font-semibold text-slate-800 hover:no-underline py-4">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-slate-600 leading-relaxed pb-4">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-12 px-4 bg-gradient-to-br from-blue-700 to-indigo-800 text-white">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-3">Чикагод суралцах мөрөөдлөө биелүүлэх цаг болсон</h2>
          <p className="text-blue-200 mb-6 text-sm">
            United Dream Mongolia — F-1 визийн мэргэжлийн зөвлөгөө. Сургуулийн сонголтоос эхлээд визийн ярилцлага хүртэл бүх алхамд тусална.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => openModal("us-study")} className="bg-white text-blue-700 hover:bg-blue-50 font-semibold">
              Үнэгүй зөвлөгөө авах
            </Button>
            <a href="tel:+97688086633">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 w-full sm:w-auto">
                📞 8808-6633
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
